import { Link } from "react-router-dom"
import { Calculator, Clock, TrendingUp, CalendarDays, Lock } from "lucide-react"
import { useLang } from "../context/LangContext"
import { useAuth } from "../context/AuthContext"

const CONTENT = {
  en: {
    toolsTitle: "Built-in tools",
    toolsSub: "Free tools available right now — no account required.",
    tools: [
      { name: "Compound Interest", desc: "Smart investment projections.", icon: Calculator, link: "/compound-interest", bg: "bg-blue-500" },
      { name: "Time Tracker", desc: "Track hours and task history.", icon: Clock, link: "/app/time-tracker", bg: "bg-emerald-500" },
      { name: "Coin Market", desc: "Real-time crypto data via CoinGecko.", icon: TrendingUp, link: "/coin-market-tracker", bg: "bg-amber-500" },
      { name: "Economic Calendar", desc: "Key market-moving events.", icon: CalendarDays, link: "/economic-calendar", bg: "bg-violet-500" },
    ],
    launch: "Open →",
  },
  es: {
    toolsTitle: "Herramientas incluidas",
    toolsSub: "Herramientas gratuitas disponibles ahora — sin necesidad de cuenta.",
    tools: [
      { name: "Interés Compuesto", desc: "Proyecciones de inversión inteligentes.", icon: Calculator, link: "/compound-interest", bg: "bg-blue-500" },
      { name: "Time Tracker", desc: "Registra horas y tareas.", icon: Clock, link: "/app/time-tracker", bg: "bg-emerald-500" },
      { name: "Mercado Cripto", desc: "Datos en tiempo real vía CoinGecko.", icon: TrendingUp, link: "/coin-market-tracker", bg: "bg-amber-500" },
      { name: "Calendario Económico", desc: "Eventos clave del mercado.", icon: CalendarDays, link: "/economic-calendar", bg: "bg-violet-500" },
    ],
    launch: "Abrir →",
  },
}

interface Props {
  className?: string
}

export default function ToolsSection({ className }: Props) {
  const { lang } = useLang()
  const { plan } = useAuth()
  const t = CONTENT[lang]
  const canAccessTimeTracker = plan === "plus" || plan === "premium" || plan === "basic"

  const card = "bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-gray-800"
  const muted = "text-gray-500 dark:text-gray-400"

  return (
    <section className={className}>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold tracking-tight">{t.toolsTitle}</h2>
        <p className={`mt-2 text-sm ${muted}`}>{t.toolsSub}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {t.tools.map((tool) => {
          const isTimeTracker = tool.link === "/app/time-tracker"
          const locked = isTimeTracker && !canAccessTimeTracker
          return (
            <Link
              key={tool.name}
              to={locked ? "/pricing" : tool.link}
              className={`group relative ${card} p-5 flex flex-col items-center text-center gap-3 active:scale-95 transition hover:shadow-md hover:-translate-y-0.5`}
            >
              <div className={`relative h-14 w-14 rounded-2xl flex items-center justify-center ${tool.bg} shadow-sm`}>
                <tool.icon className="h-7 w-7 text-white" />
                {locked && (
                  <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white dark:bg-neutral-900 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                    <Lock className="h-3 w-3 text-gray-500" />
                  </span>
                )}
              </div>
              <div>
                <div className="font-medium text-sm">{tool.name}</div>
                <p className={`mt-1 text-xs ${muted} leading-snug`}>{tool.desc}</p>
              </div>
              {locked ? (
                <span className="text-xs font-medium text-[#2563EB]">{lang === "en" ? "Upgrade" : "Mejorar"}</span>
              ) : (
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 group-hover:text-black dark:group-hover:text-white transition">
                  {t.launch}
                </span>
              )}
            </Link>
          )
        })}
      </div>
    </section>
  )
}
